import React, { useState, useContext } from "react";
import { useParams } from "react-router";
import { redirectPage } from "./Utils";
import { NotificationContext } from "./Notification";
import LoaderPR from "./LoaderPR";
import { getQueryParams } from "./Theme";
import PasswordInput from "./PasswordInput";
import SSOService from "./SSOService";
import { Form, HeaderForm, DescriptionForm, ContentForm, FooterForm } from "./Form";
import { AlertIcon } from "./icons/alert";

const CODE_LENGTH = 6;
const RESET_ERROR_MESSAGE =
  "We couldn't reset your password. Please verify the code and try again.";

function PasswordRecoveryCode() {
  const codePattern = /^\d{6}$/;
  const { email } = useParams();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [missmatch, setMissmatch] = useState(false);
  const [error, setError] = useState(false);
  const [showLoading, setLoading] = useState(false);
  const { addToast } = useContext(NotificationContext);
  const service = new SSOService();
  const queryParams = getQueryParams();
  return (
    <Form name="loginForm">
      <HeaderForm />
      <ContentForm>
        <DescriptionForm
         title="Reset Password"
         description={`Please check your email (${email}) look in both inbox and spam for your
         reset password request confirmation code (6 digit) and enter it here to
         confirm your identity.`}
        />
        <div className={`zds-form-item ${error ? "zds-form-item-error" : ""}`}>
          <label
            htmlFor="code"
            className="zds-form-label zds-form-label-large"
          >
            Code <span className="zds-form-label-required">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            value={code}
            onChange={(event) => {
              const codeInput = event.target.value
                .replace(/\D/g, "")
                .slice(0, CODE_LENGTH);
              setError(!codePattern.test(codeInput));
              setCode(codeInput);
            }}
            id="code"
            className="zds-input-textfield zds-input-textfield-large"
            aria-invalid={error}
            aria-describedby={error ? "code-error" : undefined}
          />
          {error && (
            <p
              id="code-error"
              className="zds-form-instructions zds-form-instructions-large"
            >
              <AlertIcon />{" "}
              {code === ""
                ? "Code is Required."
                : `The code must be exactly ${CODE_LENGTH} digits.`}
            </p>
          )}
        </div>
        <LoaderPR showLoading={showLoading} />

        <PasswordInput
          onChange={(newPassword) => setPassword(newPassword)}
          name="password"
          label="Please add your new password"
          validate={true}
          autoComplete={false}
        />
        <PasswordInput
          onChange={(retypeNewPassword) => {
            setMissmatch(retypeNewPassword.trim() !== password.trim());
            setRetypePassword(retypeNewPassword);
          }}
          name="retypepassword"
          label="Confirm new password"
          validate={false}
          missmatch={missmatch}
          autoComplete={false}
        />
      </ContentForm>
      <FooterForm>
        <button
          disabled={showLoading}
          className="zds-button-primary zds-button-large"
          type="submit"
          onClick={(event) => {
            event.preventDefault();
            if (!codePattern.test(code)) {
              setError(true);
              addToast(
                `Please enter the ${CODE_LENGTH}-digit code sent to your email`,
                "error"
              );
              return;
            }
            const hasError =
              retypePassword.trim() !== password.trim() ||
              password.trim() === "" ||
              retypePassword.trim() === "" ||
              missmatch;
            if (hasError) {
              addToast("All fields are required", "error");
              return;
            }
            setLoading(true);
            service
              .passwordRecovery(email, password, code)
              .then(() => {
                redirectPage(`/login${queryParams}`);
              })
              .catch((err) => {
                setLoading(false);
                const message =
                  err?.error && err.error !== "internal server error"
                    ? err.error
                    : RESET_ERROR_MESSAGE;
                addToast(message, "error");
              });
          }}
        >
          Reset
        </button>
        <button
          disabled={showLoading}
          className="zds-button-secondary zds-button-large"
          type="submit"
          onClick={(event) => {
            event.preventDefault();
            redirectPage(`/login${queryParams}`);
          }}
        >
          Cancel
        </button>
      </FooterForm>
    </Form>
  );
}

export default PasswordRecoveryCode;
